import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import GlobalChatSocket from "../components/GlobalChatSocket";

const ChatContext = createContext(null);

export const useChat = () => useContext(ChatContext);

export function ChatProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadMap, setUnreadMap] = useState({});
  const [presenceMap, setPresenceMap] = useState({});
  const [globalConnected, setGlobalConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isChatPageOpen, setIsChatPageOpen] = useState(false);

  const addNotification = useCallback((notification) => {
    if (!notification?.id) return;

    setNotifications((prev) => {
      const exists = prev.some((item) => item.id === notification.id);
      if (exists) return prev;
      return [notification, ...prev].slice(0, 50);
    });
  }, []);

  const removeNotification = useCallback((notificationId) => {
    setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const incrementUnread = useCallback((threadId) => {
    const tid = Number(threadId);
    if (!tid) return;

    setUnreadMap((prev) => ({
      ...prev,
      [tid]: Number(prev[tid] || 0) + 1,
    }));
  }, []);

  const setUnreadForThread = useCallback((threadId, count) => {
    const tid = Number(threadId);
    if (!tid) return;

    const safeCount = Math.max(0, Number(count || 0));

    setUnreadMap((prev) => {
      if (safeCount <= 0) {
        const next = { ...prev };
        delete next[tid];
        return next;
      }

      return {
        ...prev,
        [tid]: safeCount,
      };
    });
  }, []);

  const clearUnread = useCallback((threadId) => {
    const tid = Number(threadId);
    if (!tid) return;

    setUnreadMap((prev) => {
      if (!(tid in prev)) return prev;
      const next = { ...prev };
      delete next[tid];
      return next;
    });
  }, []);

  const clearAllUnread = useCallback(() => {
    setUnreadMap({});
  }, []);

  const setUserPresence = useCallback((userId, status) => {
    const uid = Number(userId);
    if (!uid) return;

    setPresenceMap((prev) => ({
      ...prev,
      [uid]: status === "online" ? "online" : "offline",
    }));
  }, []);

  const setPresenceBulk = useCallback((bulk) => {
    setPresenceMap((prev) => ({
      ...prev,
      ...(bulk || {}),
    }));
  }, []);

  const setActiveChatThread = useCallback((threadId) => {
    setActiveThreadId(threadId ? Number(threadId) : null);
  }, []);

  const setChatPageOpened = useCallback((isOpen) => {
    setIsChatPageOpen(!!isOpen);
  }, []);

  const unreadCount = useMemo(() => {
    return Object.values(unreadMap).reduce((sum, value) => sum + Number(value || 0), 0);
  }, [unreadMap]);

  const value = useMemo(
    () => ({
      notifications,
      unreadMap,
      unreadCount,
      presenceMap,
      globalConnected,
      lastEvent,

      activeThreadId,
      isChatPageOpen,

      addNotification,
      removeNotification,
      clearNotifications,

      incrementUnread,
      setUnreadForThread,
      clearUnread,
      clearAllUnread,

      setUserPresence,
      setPresenceBulk,

      setActiveChatThread,
      setChatPageOpened,

      setGlobalEvent: setLastEvent,
      setGlobalConnected,
    }),
    [
      notifications,
      unreadMap,
      unreadCount,
      presenceMap,
      globalConnected,
      lastEvent,
      activeThreadId,
      isChatPageOpen,
      addNotification,
      removeNotification,
      clearNotifications,
      incrementUnread,
      setUnreadForThread,
      clearUnread,
      clearAllUnread,
      setUserPresence,
      setPresenceBulk,
      setActiveChatThread,
      setChatPageOpened,
    ]
  );

  return (
    <ChatContext.Provider value={value}>
      <GlobalChatSocket />
      {children}
    </ChatContext.Provider>
  );
}